/**
 * Right-hand detail panel for the selected deal: headline health score,
 * the five-axis radar (engagement / deal quality / urgency / momentum /
 * multi-threading), per-axis score bars, engagement facts, and the next
 * step. Mirrors the landscape's band colors via StatusBadge.
 */
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from "recharts";

import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { DealHealthDeal } from "../api";
import { fmtCents, fmtDate } from "../format";
import {
  DEAL_HEALTH_GRID,
  DEAL_HEALTH_RADAR,
  pipelineShort,
} from "./dealHealthColors";

const AXES: { key: keyof DealHealthDeal["scores"]; label: string }[] = [
  { key: "engagement", label: "Engagement" },
  { key: "deal_quality", label: "Deal Quality" },
  { key: "urgency", label: "Urgency" },
  { key: "momentum", label: "Momentum" },
  { key: "multithreading", label: "Multi-thread" },
];

function ScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-brand"
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        />
      </div>
      <span className="w-7 shrink-0 text-right font-medium tabular-nums">
        {Math.round(value)}
      </span>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md bg-muted/50 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-xs font-medium">{value}</div>
    </div>
  );
}

export function DealHealthDetail({ deal }: { deal: DealHealthDeal | null }) {
  if (!deal) {
    return (
      <Card className="flex h-full items-center justify-center">
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          Select a deal on the landscape
          <br />
          (or in the table) to see its breakdown.
        </CardContent>
      </Card>
    );
  }

  const radarData = AXES.map((a) => ({
    axis: a.label,
    score: deal.scores[a.key],
  }));

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="truncate text-base" title={deal.name}>
              {deal.name}
            </CardTitle>
            <div className="mt-1 text-xs text-muted-foreground">
              {deal.owner_display_name ?? "Unowned"}
              {" · "}
              {deal.contact_count} contact{deal.contact_count === 1 ? "" : "s"}
              {deal.days_since_touch != null && (
                <>
                  {" · last touch "}
                  {deal.days_since_touch === 0
                    ? "today"
                    : `${deal.days_since_touch}d ago`}
                </>
              )}
              {" · "}
              {deal.amount_cents > 0 ? fmtCents(deal.amount_cents) : "no amount"}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className="text-2xl font-bold tabular-nums">
              {Math.round(deal.health_score)}
              <span className="text-xs font-normal text-muted-foreground">/100</span>
            </span>
            <StatusBadge value={deal.band} />
          </div>
        </div>
        {deal.band_reasons.length > 0 && (
          <p className="text-xs text-warning">{deal.band_reasons.join(" · ")}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="h-48">
          <ResponsiveContainer>
            <RadarChart data={radarData} outerRadius="75%">
              <PolarGrid stroke={DEAL_HEALTH_GRID} />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar
                dataKey="score"
                stroke={DEAL_HEALTH_RADAR}
                fill={DEAL_HEALTH_RADAR}
                fillOpacity={0.25}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-1.5">
          {AXES.map((a) => (
            <ScoreBar key={a.key} label={a.label} value={deal.scores[a.key]} />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          <Fact
            label="Stage"
            value={
              <>
                {deal.stage_name ?? "—"}
                <span className="text-muted-foreground">
                  {" · "}
                  {pipelineShort(deal.pipeline_name)}
                </span>
              </>
            }
          />
          <Fact
            label="Close date"
            value={
              <>
                {fmtDate(deal.close_date)}
                {deal.days_to_close != null && deal.days_to_close < 0 && (
                  <span className="text-danger"> (overdue)</span>
                )}
              </>
            }
          />
          <Fact
            label="Emails 90d"
            value={`${deal.emails_90d} (${deal.inbound_emails_90d} inbound)`}
          />
          <Fact label="Calls 90d" value={deal.calls_90d} />
          <Fact
            label="Meetings"
            value={`${deal.completed_meetings} held${
              deal.no_shows > 0 ? ` · ${deal.no_shows} no-show` : ""
            }`}
          />
          <Fact
            label="Days in stage"
            value={deal.days_in_stage != null ? `${deal.days_in_stage}d` : "—"}
          />
          {deal.competitor && deal.competitor.toLowerCase() !== "none" && (
            <Fact label="Competitor" value={deal.competitor} />
          )}
          {deal.deal_source && <Fact label="Source" value={deal.deal_source} />}
        </div>

        {deal.next_step && (
          <div className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Next step
              {deal.next_step_updated_on &&
                ` · updated ${fmtDate(deal.next_step_updated_on)}`}
            </div>
            <p className="mt-0.5 text-xs">{deal.next_step}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
